import { BankEntity } from 'models/BankEntity';
import { CompanyEntity } from 'models/CompanyEntity';
import { HedgingInstrument } from 'models/HedgingInstrument';
import { ActionType, getType } from 'typesafe-actions';

import {
  TransactionReviewStatusModes,
  TransactionStatusModes,
} from '../../../../enums';
import { TransactionTypeModes } from '../../../../enums/TransactionTypeModes';

import * as pricerActions from './../../actions';

type PricerActionType = ActionType<typeof pricerActions>;

export type TradeSummaryState = {
  transaction_type_id: null | TransactionTypeModes;
  company_entity_id: CompanyEntity['id'];
  bank_entity_id: BankEntity['bank_entity_id'];
  instrument_type_id: HedgingInstrument['instrument_id'];
  trade_date: null | string;
  display_currency_id: number;
  custom_trade_title: null | string;
  comments: null | string;
  sub_deal_name: null | string;
  // hedge accounting fields
  bloomberg_id: null | string;
  journal_entry_currency_id: null | number;
  transaction_confirmation_reference: null | string;
  hedged_item_id: null | number;
  hedged_risk_id: null | number;
  // Internal Fields
  internal_alias: null | string;
  rm_engagement: boolean | number;
  mid_market_level: null | number;
  execution_spread: null | number;
  credit_spread: null | number;
  // deal contingent modifier — the schedule itself lives on transaction.deal_contingent
  is_deal_contingent: boolean;
  // termination fields
  close_out_date: null | string;
  close_out_amount: null | number;
  mandatory_termination_date: null | string;
  //
  creator: null | string;
  creator_id: null | string;
  last_change_date: null | string;
  last_changed_by: null | string;
  review_date: null | string;
  reviewer: null | string;
  reviewer_id: null | string;
  transaction_review_status: null | keyof typeof TransactionReviewStatusModes;
  transaction_review_status_id: null | TransactionReviewStatusModes;
  transaction_status: null | TransactionStatusModes;
  parent_trade_id: null | number;
};

export const TradeSummaryInitialState: TradeSummaryState = {
  transaction_type_id: null,
  company_entity_id: '553908a6dd71e',
  bank_entity_id: '59db93650f019',
  instrument_type_id: 1,
  trade_date: null,
  display_currency_id: 1,
  custom_trade_title: null,
  comments: null,
  sub_deal_name: null,
  // hedge accounting fields
  bloomberg_id: null,
  journal_entry_currency_id: null,
  transaction_confirmation_reference: null,
  hedged_item_id: null,
  hedged_risk_id: null,
  // Internal Fields
  internal_alias: null,
  rm_engagement: true,
  mid_market_level: null,
  execution_spread: null,
  credit_spread: null,
  is_deal_contingent: false,
  // termination fields
  close_out_date: null,
  close_out_amount: null,
  mandatory_termination_date: null,
  //
  creator: null,
  creator_id: null,
  last_change_date: null,
  last_changed_by: null,
  review_date: null,
  reviewer: null,
  reviewer_id: null,
  transaction_review_status: null,
  transaction_review_status_id: null,
  transaction_status: null,
  parent_trade_id: null,
};

const tradeSummaryReducer = (
  state: TradeSummaryState = TradeSummaryInitialState,
  action: PricerActionType,
): TradeSummaryState => {
  switch (action.type) {
    case getType(pricerActions.populateTradeDefault): {
      return {
        ...state,
        transaction_type_id: TransactionTypeModes.Internal,
        company_entity_id: '553908a6dd71e', // PMC NYC
        bank_entity_id: '59db93650f019', // USD Global Bank A
        trade_date: action.payload.market_date,
        display_currency_id: action.payload.user_currency_id,
        journal_entry_currency_id: action.payload.user_currency_id,
        instrument_type_id: action.payload.instrument_type_id,
      };
    }
    case getType(pricerActions.updateTradeSummaryStateFromForm): {
      return {
        ...state,
        ...action.payload.data,
      };
    }

    default:
      return state;
  }
};

export default tradeSummaryReducer;
